/**
 * Product Hero Gallery custom Lightbox core.
 *
 * Owns Lightbox lifecycle, slide navigation, image/video rendering, and preload.
 * Gesture behavior is installed separately by the public gallery entrypoint.
 */

(() => {
  const MODULE_KEY = '__MarineMaxProductHeroGalleryLightboxCore';

  if (window[MODULE_KEY]?.install) return;

  class ProductHeroGalleryLightboxCoreBehavior {
    initializeLightboxCoreState() {
      this._pendingLightboxIndex = null;
      this.lightboxTransformFrame = null;
    }

    invalidateLightboxPendingRender() {
      this._pendingLightboxIndex = null;
    }

    destroyLightboxCore() {
      this.invalidateLightboxPendingRender();

      if (this.lightboxTransformFrame !== null && this.lightboxTransformFrame !== undefined) {
        window.cancelAnimationFrame?.(this.lightboxTransformFrame);
        this.lightboxTransformFrame = null;
      }
    }

    // Initialize lightbox
    initLightbox() {
      this.lightbox = document.querySelector(`#ProductLightbox-${this.sectionId}`);
      if (!this.lightbox) return;

      this.lightboxImage = this.lightbox.querySelector('[data-lightbox-image]');
      this.lightboxStage = this.lightbox.querySelector('[data-lightbox-stage]');
      this.lightboxVideoFrame = this.lightbox.querySelector('[data-lightbox-video-frame]');
      this.lightboxVideoNative = this.lightbox.querySelector('[data-lightbox-video-native]');
      this.lightboxVideoUrl = (this.lightbox.dataset.lightboxVideoUrl || '').trim();
      this.lightboxHasVideo = this.lightboxVideoUrl.length > 0;
      this.lightboxCurrentSpan = this.lightbox.querySelector('[data-lightbox-current]');
      this.lightboxThumbnails = this.lightbox.querySelectorAll('[data-lightbox-thumb]');
      this.lightboxPrevBtn = this.lightbox.querySelector('[data-lightbox-prev]');
      this.lightboxNextBtn = this.lightbox.querySelector('[data-lightbox-next]');
      this.lightboxCloseBtn = this.lightbox.querySelector('[data-lightbox-close]');
      this.lightboxBackBtn = this.lightbox.querySelector('[data-lightbox-back]');
      this.lightboxZoomInBtn = this.lightbox.querySelector('[data-lightbox-zoom-in]');
      this.lightboxZoomOutBtn = this.lightbox.querySelector('[data-lightbox-zoom-out]');
      this.lightboxCurrentIndex = 0;
      this.lightboxSource = 'gallery';
      this._pendingLightboxIndex = null;
      this.lightboxZoom = 1;
      this.lightboxPanX = 0;
      this.lightboxPanY = 0;
      this.lightboxPointerState = null;
      const listenerOptions = { signal: this.eventAbortController.signal };

      // Event listeners
      if (this.lightboxCloseBtn) {
        this.lightboxCloseBtn.addEventListener('click', () => this.closeLightbox(), listenerOptions);
      }

      if (this.lightboxBackBtn) {
        this.lightboxBackBtn.addEventListener(
          'click',
          () => this.closeLightbox({ returnToGallery: true }),
          listenerOptions,
        );
      }

      if (this.lightboxPrevBtn) {
        this.lightboxPrevBtn.addEventListener('click', () => this.lightboxPrev(), listenerOptions);
      }

      if (this.lightboxNextBtn) {
        this.lightboxNextBtn.addEventListener('click', () => this.lightboxNext(), listenerOptions);
      }

      // Thumbnail clicks
      if (this.lightboxThumbnails) {
        this.lightboxThumbnails.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            const index = parseInt(thumb.dataset.lightboxThumb, 10);
            this.setLightboxIndex(index);
          }, listenerOptions);
        });
      }

      if (this.lightboxZoomInBtn) {
        this.lightboxZoomInBtn.addEventListener('click', () => this.zoomLightboxBy(0.35), listenerOptions);
      }

      if (this.lightboxZoomOutBtn) {
        this.lightboxZoomOutBtn.addEventListener('click', () => this.zoomLightboxBy(-0.35), listenerOptions);
      }

      // Keyboard navigation
      this.handleLightboxKeydown = (e) => {
        if (!this.lightbox.classList.contains('is-open')) return;

        if (e.key === 'Escape') {
          this.closeLightbox({ returnToGallery: true });
        } else if (e.key === 'ArrowLeft') {
          this.lightboxPrev();
        } else if (e.key === 'ArrowRight') {
          this.lightboxNext();
        } else if (e.key === '+' || e.key === '=') {
          this.zoomLightboxBy(0.35);
        } else if (e.key === '-') {
          this.zoomLightboxBy(-0.35);
        }
      };
      document.addEventListener('keydown', this.handleLightboxKeydown, listenerOptions);

      // Close on background click
      this.lightbox.addEventListener(
        'click',
        (e) => {
          if (e.target === this.lightbox || e.target.classList.contains('product__lightbox-main')) {
            this.closeLightbox({ returnToGallery: true });
          }
        },
        listenerOptions,
      );

      this.initLightboxInteractions();
    }

    openLightbox(index, { source = 'gallery', pushHistory = true, focus = true } = {}) {
      if (!this.lightbox || (!this.imagesData.length && !this.lightboxHasVideo)) return;

      this.lightboxSource = source;
      this.lightboxCurrentIndex = index;
      this.updateLightboxImage();

      this.lightbox.classList.add('is-open');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (pushHistory) {
        const currentPayload = this.getHistoryStatePayload();
        const { replace } = this.prepareHistoryForOverlay({
          allowReplaceCurrent: currentPayload?.overlay === 'lightbox',
        });
        this.setHistoryState(
          {
            overlay: 'lightbox',
            modalType: source === 'hub' ? 'hub' : 'gallery',
            parent: source,
            view: source === 'hub' ? this.mediaHubCurrentView || 'gallery' : 'gallery',
            galleryIndex: source === 'hub' ? this.mediaHubGalleryIndex : this.lightboxCurrentIndex,
            lightboxIndex: this.lightboxCurrentIndex,
          },
          { replace },
        );
      }

      if (focus && this.lightboxBackBtn) this.lightboxBackBtn.focus();
    }

    closeLightbox({ returnToGallery = false, syncHistory = true } = {}) {
      if (!this.lightbox) return;
      if (syncHistory) {
        this.navigateBackOrClose(
          'lightbox',
          () => this.closeLightbox({ returnToGallery, syncHistory: false }),
        );
        return;
      }

      this.stopLightboxVideoPlayback();
      this.resetLightboxInteractionState?.();
      this.resetLightboxZoom();
      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      this.syncCurrentHistoryEntryToUi();
      const galleryIsOpen = !!this.galleryModal?.classList.contains('is-open');
      const hubIsOpen = !!this.mediaHubModal?.classList.contains('is-open');
      document.body.style.overflow = galleryIsOpen || hubIsOpen ? 'hidden' : '';

      if (returnToGallery && hubIsOpen) {
        const activeHubItem =
          this.mediaHubModal.querySelector('[data-hub-featured-open]') ||
          this.mediaHubModal.querySelector('[data-hub-gallery-item].is-active') ||
          this.mediaHubModal.querySelector('[data-hub-gallery-item]');
        activeHubItem?.focus?.();
        return;
      }

      if (returnToGallery && galleryIsOpen) {
        const activeGalleryImage = this.galleryModal.querySelectorAll('[data-gallery-open]')[
          this.lightboxCurrentIndex
        ];
        if (activeGalleryImage) {
          activeGalleryImage.focus?.();
        }
        return;
      }

      if (this.galleryBtn) this.galleryBtn.focus();
    }

    getLightboxItemCount() {
      return this.imagesData.length;
    }

    setLightboxIndex(index) {
      this.lightboxCurrentIndex = index;
      this.updateLightboxImage();
    }

    stopLightboxVideoPlayback() {
      if (this.lightboxVideoFrame) {
        this.pauseEmbeddedFrame(this.lightboxVideoFrame);
        this.lightboxVideoFrame.hidden = true;
      }
      if (this.lightboxVideoNative) {
        this.lightboxVideoNative.pause();
        this.lightboxVideoNative.removeAttribute('src');
        this.lightboxVideoNative.load();
        this.lightboxVideoNative.hidden = true;
      }
      if (this.lightboxImage) {
        this.lightboxImage.hidden = false;
      }
    }

    showLightboxVideo() {
      if (!this.lightboxHasVideo) return false;
      this.stopLightboxVideoPlayback();
      if (this.lightboxImage) this.lightboxImage.hidden = true;

      const embedUrl = this.getVideoEmbedUrl(this.lightboxVideoUrl);
      if (embedUrl && this.lightboxVideoFrame) {
        this.setFrameSource(this.lightboxVideoFrame, embedUrl);
        this.lightboxVideoFrame.hidden = false;
        return true;
      }

      if (this.lightboxVideoNative) {
        this.lightboxVideoNative.src = this.lightboxVideoUrl;
        this.lightboxVideoNative.hidden = false;
        return true;
      }

      return false;
    }

    preloadLightboxNeighbors(centerIndex) {
      const n = this.imagesData.length;
      if (n <= 1) return;
      const idxs = [
        (centerIndex - 1 + n) % n,
        (centerIndex + 1) % n,
      ];
      idxs.forEach((i) => {
        const d = this.imagesData[i];
        if (!d || !d.src) return;
        const im = new Image();
        im.sizes = '100vw';
        if (d.srcset) im.srcset = d.srcset;
        im.src = d.src;
      });
    }

    updateLightboxImage() {
      const totalItems = this.getLightboxItemCount();
      if (totalItems <= 0) return;
      if (this.lightboxCurrentIndex < 0 || this.lightboxCurrentIndex >= totalItems) {
        this.lightboxCurrentIndex = 0;
      }
      const isVideoSlide = this.lightboxHasVideo && this.lightboxCurrentIndex === this.imagesData.length;
      if (isVideoSlide) {
        this.showLightboxVideo();
        if (this.lightboxCurrentSpan) {
          this.lightboxCurrentSpan.textContent = this.lightboxCurrentIndex + 1;
        }
        if (this.lightboxThumbnails) {
          this.lightboxThumbnails.forEach((thumb, i) => {
            thumb.classList.toggle('is-active', i === this.lightboxCurrentIndex);
          });
        }
        return;
      }
      if (!this.lightboxImage || !this.imagesData[this.lightboxCurrentIndex]) return;
      this.stopLightboxVideoPlayback();
      this.resetLightboxZoom();

      const imgData = this.imagesData[this.lightboxCurrentIndex];
      const idx = this.lightboxCurrentIndex;
      this._pendingLightboxIndex = idx;

      const applyDom = () => {
        if (this._pendingLightboxIndex !== idx) return;
        this.lightboxImage.removeAttribute('width');
        this.lightboxImage.removeAttribute('height');
        this.lightboxImage.sizes = '100vw';
        this.lightboxImage.srcset = imgData.srcset || '';
        this.lightboxImage.src = imgData.src;
        this.lightboxImage.alt = imgData.alt;
        if (imgData.width) {
          this.lightboxImage.width = imgData.width;
        }
        if (imgData.height) {
          this.lightboxImage.height = imgData.height;
        }

        if (this.lightboxCurrentSpan) {
          this.lightboxCurrentSpan.textContent = idx + 1;
        }

        if (this.lightboxThumbnails) {
          this.lightboxThumbnails.forEach((thumb, i) => {
            thumb.classList.toggle('is-active', i === idx);
          });
          const activeThumb = this.lightbox.querySelector(
            '.product__lightbox-thumbnail.is-active',
          );
          if (activeThumb) {
            activeThumb.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }

        this.preloadLightboxNeighbors(idx);
        if (this.lightboxTransformFrame !== null) {
          window.cancelAnimationFrame?.(this.lightboxTransformFrame);
        }
        this.lightboxTransformFrame = window.requestAnimationFrame(() => {
          this.lightboxTransformFrame = null;
          if (!this.isDestroyed) this.updateLightboxTransform();
        });
      };

      try {
        const cur = this.lightboxImage.src || '';
        const next = new URL(imgData.src, window.location.href).href;
        if (cur && new URL(cur, window.location.href).href === next && this.lightboxImage.complete) {
          applyDom();
          return;
        }
      } catch (_) {
        /* fall through to loader */
      }

      const loader = new Image();
      loader.sizes = '100vw';
      if (imgData.srcset) loader.srcset = imgData.srcset;
      loader.onload = applyDom;
      loader.onerror = applyDom;
      loader.src = imgData.src;
    }

    lightboxPrev() {
      const total = this.getLightboxItemCount();
      if (!total) return;
      this.setLightboxIndex((this.lightboxCurrentIndex - 1 + total) % total);
    }

    lightboxNext() {
      const total = this.getLightboxItemCount();
      if (!total) return;
      this.setLightboxIndex((this.lightboxCurrentIndex + 1) % total);
    }
  }

  const descriptors = Object.getOwnPropertyDescriptors(ProductHeroGalleryLightboxCoreBehavior.prototype);
  delete descriptors.constructor;

  const install = (GalleryClass) => {
    if (typeof GalleryClass !== 'function') {
      throw new TypeError('Product Hero Gallery Lightbox core requires a class constructor.');
    }

    Object.defineProperties(GalleryClass.prototype, descriptors);
  };

  Object.defineProperty(window, MODULE_KEY, {
    configurable: true,
    enumerable: false,
    value: Object.freeze({ install }),
    writable: false,
  });
})();
